import { useState } from 'react';
import axios from 'axios';
import { Wrench, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { EditableText } from '../components/EditableText';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully!', {
        description: "We'll get back to you as soon as possible."
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-[#193460] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">
            <EditableText contentKey="contact.header.title" as="span">Contact Us</EditableText>
          </h1>
          <p className="text-xl text-gray-200">
            <EditableText contentKey="contact.header.subtitle" as="span" multiline>
              Have a question? We'd love to hear from you. Send us a message!
            </EditableText>
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-2 border-gray-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl text-[#193460]">
                  <EditableText contentKey="contact.form.title" as="span">Send Us a Message</EditableText>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-semibold text-[#193460] mb-2 block">
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-semibold text-[#193460] mb-2 block">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-base font-semibold text-[#193460] mb-2 block">
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="What is this regarding?"
                      className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-semibold text-[#193460] mb-2 block">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Write your message here..."
                      rows={6}
                      className="border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300 resize-none"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold h-14 text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
                  >
                    <Send className="mr-2 w-5 h-5" />
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <Link to="/repair-request#enquiry-form" className="block group" data-testid="contact-send-enquiry">
                <Card className="border-2 border-[#E87B6A] shadow-lg group-hover:shadow-xl transition-shadow duration-300 cursor-pointer">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-[#E87B6A] rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                        <Wrench className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-[#193460] mb-2">
                          <EditableText contentKey="contact.repair.title" as="span">Send an enquiry</EditableText>
                        </h3>
                        <p className="text-gray-700 group-hover:text-[#E87B6A] transition-colors duration-300">
                          <EditableText contentKey="contact.repair.cta" as="span">Open the repair enquiry form</EditableText>
                          <span aria-hidden="true"> →</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Card className="bg-gray-100 border-2 border-gray-300">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-[#193460] mb-4">
                    <EditableText contentKey="contact.hours.title" as="span">Opening Hours</EditableText>
                  </h3>
                  <div className="space-y-2 text-gray-700">
                    <p className="flex justify-between">
                      <span className="font-medium"><EditableText contentKey="contact.hours.label1" as="span">Repair Cafe Events:</EditableText></span>
                      <span><EditableText contentKey="contact.hours.value1" as="span">First Saturday of the month</EditableText></span>
                    </p>
                    <p className="flex justify-between">
                      <span className="font-medium"><EditableText contentKey="contact.hours.label2" as="span">Time:</EditableText></span>
                      <span><EditableText contentKey="contact.hours.value2" as="span">11:00 - 15:45</EditableText></span>
                    </p>
                    <p className="text-sm text-gray-600 mt-4">
                      <EditableText contentKey="contact.hours.note" as="span" multiline>
                        Please check our Facebook page for the latest event updates and any schedule changes.
                      </EditableText>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
